import React, { useEffect } from "react";
import { Grid, SelectChangeEvent } from "@mui/material";
import { Allergies, Cuisines, NoGoes, Preferences } from "assets/data";
import { Custom, Field } from "components";
import { useTheme } from "contexts";
import { IPreference } from "interfaces";
import { StringOrOptionType } from "types";
import { Helpers } from "utils";

type Props = {
  state: any;
  t: any;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onCustomChange: (name: string, value: any) => void;
  onValidation: (valid: boolean) => void;
  handlePreferenceChange: (id: string, checked: boolean) => void;
};

export const Fields = ({
  state,
  t,
  onChange,
  onValidation,
  onCustomChange,
  handlePreferenceChange,
}: Props) => {
  const { theme } = useTheme();

  return (
    <Grid container spacing={theme.spacing.md}>
      <Grid item xs={12}>
        <Custom.TextField
          type="email"
          name="email"
          required
          label={t.label.email}
          placeholder={t.placeholder.email_address}
          value={state.email}
          onChange={onChange}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Custom.TextField
          name="first_name"
          required
          label={t.label.first_name}
          placeholder={t.placeholder.first_name}
          value={state.first_name}
          onChange={onChange}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Custom.TextField
          name="last_name"
          label={t.label.last_name}
          placeholder={t.placeholder.last_name}
          value={state.last_name}
          onChange={onChange}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Custom.TextField
          type="date"
          name="birth_date"
          label={t.label.birth_date}
          value={state.birth_date}
          onChange={onChange}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Field.Search
          name="country"
          value={state.country}
          options={Helpers.countries}
          label={t.label.country}
          placeholder={t.placeholder.country}
          onOptionSelect={(selected: StringOrOptionType) =>
            onCustomChange("country", selected)
          }
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Field.Search
          name="city"
          value={state.city}
          options={state.country ? Helpers.cities(state.country) : []}
          label={t.label.city}
          placeholder={t.placeholder.city}
          onOptionSelect={(selected: StringOrOptionType) =>
            onCustomChange("city", selected)
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Field.Password
          onPasswordChange={(value) => onCustomChange("password", value)}
          onPasswordValid={onValidation}
        />
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          {Preferences.map((item: IPreference) => (
            <Grid key={item._id} item xs={6} sm={4}>
              <Field.ImageCheckbox
                id={item._id}
                src={item.src}
                label={item.name}
                checked={state.preferences.includes(item._id)}
                onChange={handlePreferenceChange}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Custom.TextField
          name="dietary_preference"
          multiline
          rows={4}
          fullWidth
          label={t.label.dietary_preference}
          placeholder={t.placeholder.enter_text}
          value={state.dietary_preference || ""}
          onChange={onChange}
        />
      </Grid>
      <Grid item xs={12}>
        <Field.DynamicAutocomplete
          name="allergy"
          label={t.label.allergy}
          placeholder={t.placeholder.allergy}
          options={Allergies}
          value={state.allergy || []}
          onChange={(values: StringOrOptionType[]) =>
            onCustomChange("allergy", values as string[])
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Field.DynamicAutocomplete
          name="no_go"
          label={t.label.no_go}
          placeholder={t.placeholder.enter_text}
          options={NoGoes}
          value={state.no_go || []}
          onChange={(values: StringOrOptionType[]) =>
            onCustomChange("no_go", values as string[])
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Field.DynamicAutocomplete
          name="cuisines"
          label={t.label.my_favorite_cuisine}
          placeholder={t.placeholder.enter_text}
          options={Cuisines}
          value={state.cuisines || []}
          onChange={(values: StringOrOptionType[]) =>
            onCustomChange("cuisines", values as string[])
          }
        />
      </Grid>
    </Grid>
  );
};
